import {useEffect, useMemo, useRef, useState} from 'react';
import dayjs from 'dayjs';
import {FiCalendar, FiCheck, FiChevronDown, FiUsers} from 'react-icons/fi';
import {DateRangeInput} from '@ui/molecules/date-range-input';
import {
  DashboardMember,
  DashboardPeriod,
  DashboardScope,
} from './manager-dashboard.types';
import {
  CompactFilterButton,
  Controls,
  DateField,
  DateFieldButton,
  DateMenu,
  DateMenuAction,
  DateMenuBody,
  DateMenuCancel,
  DateMenuFooter,
  DateMenuHeader,
  DateOption,
  DateOptionMenu,
  DateRangeField,
  FilterControl,
  FilterDivider,
  ScopeMenu,
  ScopeMenuHeading,
  ScopeMenuSection,
  ScopeMenuSeparator,
  ScopeOption,
} from './manager-dashboard.styles';

type OpenMenu = 'scope' | 'person' | 'date' | null;
type OpenDateMenu = 'year' | 'quarter' | null;

interface ManagerDashboardFiltersProps {
  scopes: DashboardScope[];
  scope: DashboardScope;
  period: DashboardPeriod;
  onScopeChange: (scopeId: string) => void;
  onPeriodChange: (periodId: string) => void;
  members?: DashboardMember[];
  person?: DashboardMember;
  onPersonChange?: (personId: string) => void;
}

export const ManagerDashboardFilters = ({
  scopes,
  scope,
  period,
  onScopeChange,
  onPeriodChange,
  members = [],
  person,
  onPersonChange,
}: ManagerDashboardFiltersProps) => {
  const controlsRef = useRef<HTMLDivElement>(null);
  const [openMenu, setOpenMenu] = useState<OpenMenu>(null);
  const [openDateMenu, setOpenDateMenu] = useState<OpenDateMenu>(null);
  const [draftYear, setDraftYear] = useState(period.year);
  const [draftQuarter, setDraftQuarter] = useState(period.quarter);
  const [customRange, setCustomRange] = useState({
    starts: period.starts,
    ends: period.ends,
  });
  const groupScopes = scopes.filter((option) => option.type === 'group');
  const directScope = scopes.find((option) => option.type === 'direct');
  const workspaceScope = scopes.find((option) => option.type === 'workspace');
  const selectingPerson = Boolean(person);
  const selectorMenu: OpenMenu = selectingPerson ? 'person' : 'scope';
  const selectorLabel = person?.name || scope.label;
  const hasMultipleOptions = selectingPerson
    ? members.length > 1
    : scopes.length > 1;
  const years = useMemo(() => {
    const currentYear = dayjs().year();
    return Array.from(
      new Set([
        currentYear + 1,
        currentYear,
        currentYear - 1,
        currentYear - 2,
        period.year,
      ]),
    ).sort((a, b) => b - a);
  }, [period.year]);

  useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!controlsRef.current?.contains(event.target as Node)) {
        setOpenMenu(null);
        setOpenDateMenu(null);
      }
    };
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpenMenu(null);
        setOpenDateMenu(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  const openDate = () => {
    setDraftYear(period.year);
    setDraftQuarter(period.quarter);
    setCustomRange({starts: period.starts, ends: period.ends});
    setOpenDateMenu(null);
    setOpenMenu((current) => (current === 'date' ? null : 'date'));
  };

  const chooseScope = (scopeId: string) => {
    onScopeChange(scopeId);
    setOpenMenu(null);
  };

  const choosePerson = (personId: string) => {
    onPersonChange?.(personId);
    setOpenMenu(null);
  };

  const scopeOption = (option: DashboardScope, label = option.label) => (
    <ScopeOption
      key={option.id}
      type="button"
      role="option"
      $active={scope.id === option.id}
      aria-selected={scope.id === option.id}
      onClick={() => chooseScope(option.id)}>
      {label} ({option.count})
    </ScopeOption>
  );

  return (
    <Controls ref={controlsRef} aria-label="Dashboard filters">
      <FilterControl>
        <CompactFilterButton
          type="button"
          $open={hasMultipleOptions && openMenu === selectorMenu}
          $inactive={!hasMultipleOptions}
          disabled={!hasMultipleOptions}
          aria-label={
            hasMultipleOptions
              ? selectingPerson
                ? 'Select team member'
                : 'Select reporting scope'
              : selectingPerson
              ? `Selected team member: ${selectorLabel}`
              : `Reporting scope: ${selectorLabel}`
          }
          aria-haspopup={hasMultipleOptions ? 'listbox' : undefined}
          aria-expanded={
            hasMultipleOptions ? openMenu === selectorMenu : undefined
          }
          onClick={
            hasMultipleOptions
              ? () => {
                  setOpenDateMenu(null);
                  setOpenMenu((current) =>
                    current === selectorMenu ? null : selectorMenu,
                  );
                }
              : undefined
          }>
          <FiUsers size={16} aria-hidden="true" />
          <span>{selectorLabel}</span>
          <FiChevronDown size={16} aria-hidden="true" />
        </CompactFilterButton>
        {hasMultipleOptions && openMenu === selectorMenu && (
          <ScopeMenu
            role="listbox"
            aria-label={
              selectingPerson
                ? 'Team member options'
                : 'Reporting scope options'
            }>
            {selectingPerson
              ? members.map((member) => (
                  <ScopeOption
                    key={member.id}
                    type="button"
                    role="option"
                    $active={person?.id === member.id}
                    aria-selected={person?.id === member.id}
                    onClick={() => choosePerson(member.id)}>
                    {member.name}
                  </ScopeOption>
                ))
              : directScope && (
                  <ScopeMenuSection>
                    {scopeOption(directScope, 'My Direct Reports')}
                  </ScopeMenuSection>
                )}
            {!selectingPerson && groupScopes.length > 0 && (
              <>
                <ScopeMenuSeparator />
                <ScopeMenuSection>
                  <ScopeMenuHeading>Workspace Groups</ScopeMenuHeading>
                  {groupScopes.map((option) => scopeOption(option))}
                </ScopeMenuSection>
              </>
            )}
            {!selectingPerson && workspaceScope && (
              <>
                <ScopeMenuSeparator />
                <ScopeMenuSection>
                  {scopeOption(workspaceScope)}
                </ScopeMenuSection>
              </>
            )}
          </ScopeMenu>
        )}
      </FilterControl>

      <FilterDivider aria-hidden="true" />

      <FilterControl>
        <CompactFilterButton
          type="button"
          $open={openMenu === 'date'}
          aria-label="Select reporting quarter"
          aria-haspopup="dialog"
          aria-expanded={openMenu === 'date'}
          onClick={openDate}>
          <FiCalendar size={16} aria-hidden="true" />
          <span>{period.label}</span>
          <FiChevronDown size={16} aria-hidden="true" />
        </CompactFilterButton>
        {openMenu === 'date' && (
          <DateMenu role="dialog" aria-label="Select reporting quarter">
            <DateMenuHeader>
              <span>Select date</span>
              <DateMenuCancel
                type="button"
                onClick={() => {
                  setOpenDateMenu(null);
                  setOpenMenu(null);
                }}>
                Cancel
              </DateMenuCancel>
            </DateMenuHeader>
            <DateMenuBody>
              <DateField>
                <span>Year</span>
                <DateFieldButton
                  type="button"
                  $open={openDateMenu === 'year'}
                  aria-label="Select year"
                  aria-haspopup="listbox"
                  aria-expanded={openDateMenu === 'year'}
                  onClick={() =>
                    setOpenDateMenu((current) =>
                      current === 'year' ? null : 'year',
                    )
                  }>
                  <span>{draftYear}</span>
                  <FiChevronDown size={16} />
                </DateFieldButton>
                {openDateMenu === 'year' && (
                  <DateOptionMenu role="listbox" aria-label="Year options">
                    {years.map((year) => (
                      <DateOption
                        key={year}
                        type="button"
                        role="option"
                        $active={draftYear === year}
                        aria-selected={draftYear === year}
                        onClick={() => {
                          setDraftYear(year);
                          setOpenDateMenu(null);
                        }}>
                        <span>{year}</span>
                        {draftYear === year && <FiCheck size={14} />}
                      </DateOption>
                    ))}
                  </DateOptionMenu>
                )}
              </DateField>
              <DateField>
                <span>Quarter</span>
                <DateFieldButton
                  type="button"
                  $open={openDateMenu === 'quarter'}
                  aria-label="Select quarter"
                  aria-haspopup="listbox"
                  aria-expanded={openDateMenu === 'quarter'}
                  onClick={() =>
                    setOpenDateMenu((current) =>
                      current === 'quarter' ? null : 'quarter',
                    )
                  }>
                  <span>
                    {draftQuarter === 'custom'
                      ? 'Custom range'
                      : `Q${draftQuarter} ${draftYear}`}
                  </span>
                  <FiChevronDown size={16} />
                </DateFieldButton>
                {openDateMenu === 'quarter' && (
                  <DateOptionMenu role="listbox" aria-label="Quarter options">
                    {[1, 2, 3, 4].map((quarter) => (
                      <DateOption
                        key={quarter}
                        type="button"
                        role="option"
                        $active={draftQuarter === quarter}
                        aria-selected={draftQuarter === quarter}
                        onClick={() => {
                          setDraftQuarter(quarter);
                          setOpenDateMenu(null);
                        }}>
                        <span>
                          Q{quarter} {draftYear}
                        </span>
                        {draftQuarter === quarter && <FiCheck size={14} />}
                      </DateOption>
                    ))}
                    <DateOption
                      type="button"
                      role="option"
                      $active={draftQuarter === 'custom'}
                      aria-selected={draftQuarter === 'custom'}
                      onClick={() => {
                        setDraftQuarter('custom');
                        setOpenDateMenu(null);
                      }}>
                      <span>Custom range</span>
                      {draftQuarter === 'custom' && <FiCheck size={14} />}
                    </DateOption>
                  </DateOptionMenu>
                )}
              </DateField>
              {draftQuarter === 'custom' && (
                <DateRangeField>
                  <DateRangeInput
                    name="reporting-period"
                    value={customRange}
                    block
                    compact
                    placeholder="Select date"
                    state={
                      dayjs(customRange.ends).isBefore(
                        dayjs(customRange.starts),
                        'day',
                      )
                        ? 'error'
                        : 'default'
                    }
                    helper={
                      dayjs(customRange.ends).isBefore(
                        dayjs(customRange.starts),
                        'day',
                      )
                        ? 'Start date must be before end date'
                        : ''
                    }
                    setValue={({starts, ends}) =>
                      setCustomRange({
                        starts: dayjs(starts).format('MM/DD/YYYY'),
                        ends: dayjs(ends).format('MM/DD/YYYY'),
                      })
                    }
                  />
                </DateRangeField>
              )}
            </DateMenuBody>
            <DateMenuFooter>
              <DateMenuAction
                type="button"
                $primary
                disabled={
                  draftQuarter === 'custom' &&
                  (!customRange.starts ||
                    !customRange.ends ||
                    dayjs(customRange.ends).isBefore(
                      dayjs(customRange.starts),
                      'day',
                    ))
                }
                onClick={() => {
                  onPeriodChange(
                    draftQuarter === 'custom'
                      ? `custom:${customRange.starts}:${customRange.ends}`
                      : `${draftYear}-Q${draftQuarter}`,
                  );
                  setOpenDateMenu(null);
                  setOpenMenu(null);
                }}>
                Apply
              </DateMenuAction>
            </DateMenuFooter>
          </DateMenu>
        )}
      </FilterControl>
    </Controls>
  );
};
